import { useCallback, useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";

import api from "../../services/api";
import Template from "../../template";
import * as S from "./styled";

/*
  Produção por corretor num período.

  Existe porque essa pergunta vinha sendo respondida pela listagem de redações,
  que filtra por data de ENVIO do aluno — resposta de outra pergunta. Aqui a
  definição é uma só e está escrita na tela, para o número ser defensável numa
  conversa com a equipe.

  Backend: GET /relatorios/produtividade?de=&ate=&corretor=
*/

interface Envolvido {
  corretor_id: number;
  corretor: string;
  quantidade: number;
}

interface Linha {
  corretor_id: number;
  corretor: string;
  enem: number;
  uerj: number;
  total: number;
  edicoes_em_correcoes_de_outros: number;
  editadas_por_outros: number;
  /* de quem eram as correções que ela editou / quem editou as dela */
  editou_de: Envolvido[];
  editada_por: Envolvido[];
}

/*
  O número sozinho não resolve: "2 correções suas foram editadas" só vira
  informação útil quando diz por quem. Mostra até dois nomes e resume o resto,
  para a linha não crescer sem limite.
*/
interface Detalhe {
  titulo: string;
  lista: Envolvido[];
}

function Nomes({
  lista,
  titulo,
  aoVerTodos,
}: {
  lista: Envolvido[];
  titulo: string;
  aoVerTodos: (d: Detalhe) => void;
}) {
  if (!lista || lista.length === 0) return null;

  /* três nomes já ocupam a linha toda; o resto abre no modal */
  const visiveis = lista.slice(0, 3);
  const resto = lista.length - visiveis.length;

  return (
    <>
      {visiveis.map((e) => (
        <S.Etiqueta key={e.corretor_id} title={`${e.corretor}: ${e.quantidade}`}>
          {e.corretor}
          {e.quantidade > 1 && <strong>({e.quantidade})</strong>}
        </S.Etiqueta>
      ))}

      {resto > 0 && (
        <S.BotaoResto type="button" onClick={() => aoVerTodos({ titulo, lista })}>
          +{resto} {resto === 1 ? "outro" : "outros"} · ver lista
        </S.BotaoResto>
      )}
    </>
  );
}

interface Totais {
  corretores: number;
  enem: number;
  uerj: number;
  total: number;
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* mês corrente por padrão: é o recorte que a coordenação usa para fechar */
const primeiroDiaDoMes = () => {
  const hoje = new Date();
  return iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
};

const dataBR = (valor: string) => valor.split("-").reverse().join("/");

function Produtividade() {
  const [de, setDe] = useState(primeiroDiaDoMes());
  const [ate, setAte] = useState(iso(new Date()));
  const [corretor, setCorretor] = useState("");

  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [totais, setTotais] = useState<Totais | null>(null);
  const [periodo, setPeriodo] = useState({ de: "", ate: "" });
  const [carregando, setCarregando] = useState(false);

  /* lista completa de nomes, aberta pelo "+N" quando não cabe na célula */
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);

  const carregar = useCallback(async () => {
    if (de > ate) {
      toast.error("A data inicial não pode ser maior que a final.");
      return;
    }

    setCarregando(true);

    try {
      const params: Record<string, string> = { de, ate };
      if (corretor.trim()) params.corretor = corretor.trim();

      const { data } = await api.get("/relatorios/produtividade", { params });

      setLinhas(data.data || []);
      setTotais(data.totais || null);
      setPeriodo(data.periodo || { de, ate });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Não foi possível carregar o relatório."
      );
      setLinhas([]);
      setTotais(null);
    } finally {
      setCarregando(false);
    }
  }, [de, ate, corretor]);

  /* carrega uma vez ao abrir; depois só quando o usuário manda */
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function periodoRapido(mesesAtras: number) {
    const hoje = new Date();
    const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras, 1);
    const fim = mesesAtras === 0 ? hoje : new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras + 1, 0);
    setDe(iso(inicio));
    setAte(iso(fim));
  }

  /*
    Exportação em CSV: é o formato que abre no Excel sem intermediário. O ponto e
    vírgula é o separador que o Excel em português espera; com vírgula, tudo cai
    numa coluna só.
  */
  function exportarCSV() {
    if (linhas.length === 0) {
      toast.info("Não há dados para exportar.");
      return;
    }

    const cabecalho = [
      "Corretor", "ENEM", "UERJ", "Total",
      "Ajustou correcoes de outros", "De quem",
      "Teve correcoes ajustadas por outros", "Por quem",
    ];

    const juntar = (lista: Envolvido[]) =>
      `"${lista.map((e) => `${e.corretor} (${e.quantidade})`).join(", ").replace(/"/g, '""')}"`;

    const corpo = linhas.map((l) => [
      `"${l.corretor.replace(/"/g, '""')}"`,
      l.enem, l.uerj, l.total,
      l.edicoes_em_correcoes_de_outros, juntar(l.editou_de),
      l.editadas_por_outros, juntar(l.editada_por),
    ].join(";"));

    const conteudo = [
      `Producao por corretor;${dataBR(periodo.de)} a ${dataBR(periodo.ate)}`,
      "",
      cabecalho.join(";"),
      ...corpo,
      "",
      `TOTAL;${totais?.enem ?? 0};${totais?.uerj ?? 0};${totais?.total ?? 0};;;;`,
    ].join("\n");

    /* BOM: sem ele o Excel abre os acentos errados */
    const blob = new Blob(["﻿" + conteudo], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `producao-corretores-${periodo.de}-a-${periodo.ate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Template>
      <S.Pagina>
        <S.Cabecalho>
          <S.Identidade>
            <S.IconeTitulo>
              <i className="fas fa-chart-column" />
            </S.IconeTitulo>
            <div>
              <h1>Produção por corretor</h1>
              <p>
                Quantas redações cada corretor <strong>corrigiu</strong> no período —
                contadas pela data da correção, não pela data em que o aluno enviou.
              </p>
            </div>
          </S.Identidade>

          <S.Contadores>
            <S.Contador $cor="#024995" $fundo="#eef6fd" $borda="#c5e0f7">
              <strong>{carregando ? "—" : totais?.total ?? 0}</strong>
              <span>Correções</span>
            </S.Contador>
            <S.Contador $cor="#1b7a4b" $fundo="#eafaf1" $borda="#b7e4c7">
              <strong>{carregando ? "—" : totais?.enem ?? 0}</strong>
              <span>ENEM</span>
            </S.Contador>
            <S.Contador $cor="#a35b00" $fundo="#fff8ec" $borda="#ffd8a8">
              <strong>{carregando ? "—" : totais?.uerj ?? 0}</strong>
              <span>UERJ</span>
            </S.Contador>
            <S.Contador $cor="#5b6f85" $fundo="#f4f6f9" $borda="#e0e7ef">
              <strong>{carregando ? "—" : totais?.corretores ?? 0}</strong>
              <span>Corretores</span>
            </S.Contador>
          </S.Contadores>
        </S.Cabecalho>

        <S.Filtros>
          <S.Campo>
            <label>Corrigidas a partir de</label>
            <div className="entrada">
              <i className="fas fa-calendar-day" />
              <input type="date" value={de} onChange={(e) => setDe(e.target.value)} />
            </div>
          </S.Campo>

          <S.Campo>
            <label>Corrigidas até</label>
            <div className="entrada">
              <i className="fas fa-calendar-check" />
              <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
            </div>
          </S.Campo>

          <S.Campo>
            <label>Corretor (opcional)</label>
            <div className="entrada">
              <i className="fas fa-user-pen" />
              <input
                placeholder="Nome do corretor"
                value={corretor}
                onChange={(e) => setCorretor(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && carregar()}
              />
            </div>
          </S.Campo>

          <S.Botao $primario onClick={carregar} disabled={carregando}>
            <i className="fas fa-magnifying-glass" />
            {carregando ? "Buscando…" : "Gerar"}
          </S.Botao>

          <S.Botao onClick={exportarCSV} disabled={carregando || linhas.length === 0}>
            <i className="fas fa-file-csv" />
            Exportar CSV
          </S.Botao>
        </S.Filtros>

        <S.Atalhos>
          <button type="button" onClick={() => periodoRapido(0)}>Este mês</button>
          <button type="button" onClick={() => periodoRapido(1)}>Mês passado</button>
          <button
            type="button"
            onClick={() => {
              const hoje = new Date();
              setDe(iso(new Date(hoje.getFullYear(), 0, 1)));
              setAte(iso(hoje));
            }}
          >
            Este ano
          </button>
        </S.Atalhos>

        <S.Nota>
          <i className="fas fa-circle-info" />
          <div>
            <strong>Como ler as colunas.</strong> O total é a produção da pessoa: conta
            as correções em que ela foi a <strong>primeira</strong> a trabalhar — quem
            marca a redação ou finaliza antes de todos fica com o crédito, e isso não
            muda depois.
            <div style={{ marginTop: 6 }}>
              <strong>Ajustou correções de outros:</strong> abriu uma correção creditada
              a outro corretor e alterou alguma coisa nela. Esse trabalho <em>não</em>{" "}
              entra no total — o crédito continua com quem corrigiu primeiro. As
              etiquetas azuis abaixo do número são <strong>de quem eram</strong> essas
              correções.
            </div>
            <div style={{ marginTop: 4 }}>
              <strong>Teve correções ajustadas por outros:</strong> o contrário — outro
              corretor mexeu depois em correções creditadas a esta pessoa. O total dela
              não muda por isso. As etiquetas azuis abaixo do número são{" "}
              <strong>quem mexeu</strong> nessas correções.
            </div>
            <div style={{ marginTop: 6, color: "#8698ab" }}>
              Em qualquer das duas colunas, o número entre parênteses na etiqueta é
              quantas correções foram com aquela pessoa; sem parênteses, foi uma só.
              Quando há mais de três nomes, o botão <em>“+N · ver lista”</em> abre a
              lista completa. As duas valem para ENEM e UERJ, e contam redações: se
              duas pessoas ajustaram a mesma correção, o número grande sobe 1 e as
              duas aparecem como etiqueta.
            </div>
            <div style={{ marginTop: 6, color: "#8698ab" }}>
              Em correções antigas, anteriores ao registro de histórico por ação, só
              é possível saber o último ajuste de cada redação — se mais de uma pessoa
              mexeu, aparece apenas a última.
            </div>
            <div style={{ marginTop: 6, color: "#8698ab" }}>
              As duas colunas servem para explicar divergências de contagem: quando um
              corretor acha que fez mais do que o relatório mostra, costuma ser trabalho
              que entrou como ajuste na correção de outra pessoa.
            </div>
          </div>
        </S.Nota>

        <S.Tabela>
          <S.Titulos>
            <span>Corretor</span>
            <span>ENEM</span>
            <span>UERJ</span>
            <span>Total</span>
            <span title="Correções cujo crédito é de outro corretor e que esta pessoa abriu e alterou depois. Não contam na produção dela.">
              Ajustou correções de outros
            </span>
            <span title="Correções creditadas a esta pessoa que outro corretor abriu e alterou depois. O crédito continua sendo dela.">
              Teve correções ajustadas por outros
            </span>
          </S.Titulos>

          {carregando ? (
            <S.Carregando>Levantando as correções…</S.Carregando>
          ) : linhas.length === 0 ? (
            <S.Vazio>
              <i className="fas fa-inbox" />
              <h3>Nenhuma correção no período</h3>
              <p>Ajuste as datas ou limpe o filtro por corretor.</p>
            </S.Vazio>
          ) : (
            linhas.map((l) => (
              <S.Linha key={l.corretor_id}>
                <S.Celula>
                  <span className="rotulo">Corretor</span>
                  <div className="nome">{l.corretor}</div>
                  <div className="apoio">ID: {l.corretor_id}</div>
                </S.Celula>
                <S.Celula>
                  <span className="rotulo">ENEM</span>
                  <span className="numero">{l.enem}</span>
                </S.Celula>
                <S.Celula>
                  <span className="rotulo">UERJ</span>
                  <span className="numero">{l.uerj}</span>
                </S.Celula>
                <S.Celula>
                  <span className="rotulo">Total</span>
                  <span className="forte">{l.total}</span>
                </S.Celula>
                <S.Celula>
                  <span className="rotulo">Ajustou correções de outros</span>
                  <span className="numero">{l.edicoes_em_correcoes_de_outros}</span>
                  <Nomes
                    lista={l.editou_de}
                    titulo={`Correções de outros que ${l.corretor} ajustou`}
                    aoVerTodos={setDetalhe}
                  />
                </S.Celula>
                <S.Celula>
                  <span className="rotulo">Teve correções ajustadas por outros</span>
                  <span className="numero">{l.editadas_por_outros}</span>
                  <Nomes
                    lista={l.editada_por}
                    titulo={`Quem ajustou correções de ${l.corretor}`}
                    aoVerTodos={setDetalhe}
                  />
                </S.Celula>
              </S.Linha>
            ))
          )}
        </S.Tabela>
        <Modal show={!!detalhe} onHide={() => setDetalhe(null)} centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1.05rem" }}>
              <i className="fas fa-user-pen text-primary mr-2" />
              {detalhe?.titulo}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {detalhe?.lista.map((e) => (
              <S.ItemDetalhe key={e.corretor_id}>
                <span className="nome">{e.corretor}</span>
                <span className="quantas">
                  {e.quantidade} {e.quantidade === 1 ? "correção" : "correções"}
                </span>
              </S.ItemDetalhe>
            ))}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setDetalhe(null)}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
      </S.Pagina>
    </Template>
  );
}

export default Produtividade;
